/**
 * « Compte résilié » : le bandeau d'une entreprise dont le staff a posé la
 * résiliation (`20260301006000_tenant_termination`), sur tous les écrans.
 *
 * Deux temps. Avant la date d'effet, il annonce ce qui va se passer et quand.
 * Pendant les trente jours de lecture seule, il dit la seule chose qui compte
 * encore : la date de suppression, et où télécharger ses données.
 */
import { Link } from "react-router-dom";
import { Archive, CalendarClock } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useTenantTermination } from "@/hooks/useTenantTermination";

const longDate = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** « 31 octobre 2026 » pour une date AAAA-MM-JJ, sans glissement de fuseau. */
function jour(iso: string): string {
  return longDate.format(new Date(`${iso}T00:00:00Z`));
}

function dans(days: number): string {
  if (days === 0) return "aujourd'hui";
  if (days === 1) return "demain";
  return `dans ${days} jours`;
}

export function TerminationBanner() {
  const state = useTenantTermination();
  const estPatron = useAuthStore((s) => s.user?.role === "patron");

  if (!state) return null;

  const lienExport = estPatron ? (
    <Link
      to="/parametres"
      className="inline-flex items-center gap-1.5 font-medium text-text-primary underline underline-offset-2"
    >
      <Archive size={14} aria-hidden />
      Télécharger mes données
    </Link>
  ) : null;

  if (state.phase === "annoncee") {
    return (
      <div
        role="status"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-warning/30 bg-warning/10 px-4 py-2.5 text-sm lg:px-6"
      >
        <CalendarClock size={16} className="text-warning-ink flex-shrink-0" aria-hidden />
        <p className="flex-1 min-w-[14rem] text-text-secondary">
          <span className="font-medium text-text-primary">
            Résiliation au {jour(state.effectiveOn)}
          </span>{" "}
          ({dans(state.daysUntilEffect)}). À partir de cette date, le compte passe en lecture
          seule pendant 30&nbsp;jours, puis ses données sont supprimées le {jour(state.purgeOn)}.
        </p>
        {lienExport}
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-danger/30 bg-danger/10 px-4 py-2.5 text-sm lg:px-6"
    >
      <Archive size={16} className="text-danger-ink flex-shrink-0" aria-hidden />
      <p className="flex-1 min-w-[14rem] text-text-secondary">
        <span className="font-medium text-text-primary">Compte résilié, en lecture seule.</span>{" "}
        Tout reste consultable, rien ne peut plus être modifié ni envoyé. Les données seront
        supprimées le {jour(state.purgeOn)} ({dans(state.daysUntilPurge)}).
      </p>
      {lienExport}
    </div>
  );
}
